import { useRef, useState } from 'react'

import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

interface OpenArgs {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

// Open state (controlled by `open`, else owned here) plus the shared Escape
// stack registration every mode needs: one Escape closes only the innermost
// of several nested overlays (many of which are still hook-based). Its
// capture-phase listener stops the event, so Base UI's own Escape handling
// never sees it.
export const useSelectOpen = <E extends HTMLElement>({
  open,
  defaultOpen = false,
  onOpenChange,
}: OpenArgs): {
  isOpen: boolean
  setOpen: (next: boolean) => void
  focusRef: React.MutableRefObject<E | null>
} => {
  const [inner, setInner] = useState(defaultOpen)
  const focusRef = useRef<E | null>(null)
  const isOpen = open ?? inner

  const setOpen = (next: boolean): void => {
    setInner(next)
    onOpenChange?.(next)
  }

  useEscapeDismiss({
    isOpen,
    onClose: () => {
      setOpen(false)
      focusRef.current?.focus()
    },
  })

  return { isOpen, setOpen, focusRef }
}
